import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import { useToast } from '../contexts/ToastContext'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Textarea from './Textarea'
import { Users, Search, X, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { getInitials } from '../utils/helpers'

const ClientMerge = ({ onMergeComplete }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedClients, setSelectedClients] = useState([])
  const [targetClient, setTargetClient] = useState(null)
  const [mergeReason, setMergeReason] = useState('')
  const [merging, setMerging] = useState(false)
  const organizationId = useOrganizationId()
  const { canWrite, isAdmin } = useOrganization()
  const { success, error: showError } = useToast()

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchClients()
    } else {
      setSearchResults([])
    }
  }, [searchTerm])

  const searchClients = async () => {
    if (!organizationId) return
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('organization_id', organizationId)
        .or(
          `first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`
        )
        .limit(10)

      if (error) throw error
      setSearchResults(data || [])
    } catch (error) {
      console.error('Error searching clients:', error)
    }
  }

  const toggleClient = (client) => {
    if (selectedClients.find((c) => c.id === client.id)) {
      setSelectedClients(selectedClients.filter((c) => c.id !== client.id))
      if (targetClient?.id === client.id) {
        setTargetClient(null)
      }
    } else {
      setSelectedClients([...selectedClients, client])
      if (!targetClient) {
        setTargetClient(client)
      }
    }
  }

  const setAsTarget = (client) => {
    setTargetClient(client)
  }

  const handleMerge = async () => {
    if (selectedClients.length < 2 || !targetClient || !mergeReason.trim() || !organizationId) {
      showError('Please select at least 2 clients, set a target, and provide a merge reason.', {
        title: 'Merge Requirements',
      })
      return
    }

    if (!confirm(`Merge ${selectedClients.length - 1} client(s) into ${targetClient.first_name} ${targetClient.last_name}? This cannot be undone.`)) {
      return
    }

    try {
      setMerging(true)
      const { data: { user } } = await supabase.auth.getUser()

      // Get all related data for source clients
      const sourceClientIds = selectedClients.filter((c) => c.id !== targetClient.id).map((c) => c.id)
      const mergeData = {}

      // Fetch related records
      const [carePlans, therapies, sessions, examinations, communications, documents, tags] = await Promise.all([
        supabase.from('care_plans').select('*').in('client_id', sourceClientIds),
        supabase.from('therapies').select('*').in('client_id', sourceClientIds),
        supabase.from('sessions').select('*').in('client_id', sourceClientIds),
        supabase.from('examinations').select('*').in('client_id', sourceClientIds),
        supabase.from('client_communications').select('*').in('client_id', sourceClientIds),
        supabase.from('client_documents').select('*').in('client_id', sourceClientIds),
        supabase
          .from('client_tag_assignments')
          .select('*')
          .in('client_id', sourceClientIds),
      ])

      mergeData.care_plans = carePlans.data || []
      mergeData.therapies = therapies.data || []
      mergeData.sessions = sessions.data || []
      mergeData.examinations = examinations.data || []
      mergeData.communications = communications.data || []
      mergeData.documents = documents.data || []
      mergeData.tags = tags.data || []

      // Update all related records to point to target client
      for (const sourceId of sourceClientIds) {
        // Update care plans
        if (mergeData.care_plans.length > 0) {
          await supabase.from('care_plans').update({ client_id: targetClient.id }).eq('client_id', sourceId)
        }

        // Update therapies
        if (mergeData.therapies.length > 0) {
          await supabase.from('therapies').update({ client_id: targetClient.id }).eq('client_id', sourceId)
        }

        // Update sessions
        if (mergeData.sessions.length > 0) {
          await supabase.from('sessions').update({ client_id: targetClient.id }).eq('client_id', sourceId)
        }

        // Update examinations
        if (mergeData.examinations.length > 0) {
          await supabase.from('examinations').update({ client_id: targetClient.id }).eq('client_id', sourceId)
        }

        // Update communications
        if (mergeData.communications.length > 0) {
          await supabase
            .from('client_communications')
            .update({ client_id: targetClient.id })
            .eq('client_id', sourceId)
        }

        // Update documents
        if (mergeData.documents.length > 0) {
          await supabase.from('client_documents').update({ client_id: targetClient.id }).eq('client_id', sourceId)
        }

        // Update tag assignments (remove duplicates first)
        if (mergeData.tags.length > 0) {
          // Get existing tags for target
          const { data: existingTags } = await supabase
            .from('client_tag_assignments')
            .select('tag_id')
            .eq('client_id', targetClient.id)

          const existingTagIds = new Set(existingTags?.map((t) => t.tag_id) || [])

          // Update tag assignments
          for (const tag of mergeData.tags.filter((t) => t.client_id === sourceId)) {
            if (!existingTagIds.has(tag.tag_id)) {
              await supabase
                .from('client_tag_assignments')
                .update({ client_id: targetClient.id })
                .eq('id', tag.id)
            } else {
              // Delete duplicate
              await supabase.from('client_tag_assignments').delete().eq('id', tag.id)
            }
          }
        }

        // Log the merge
        await supabase.from('client_merge_log').insert({
          organization_id: organizationId,
          source_client_id: sourceId,
          target_client_id: targetClient.id,
          merged_by: user?.id,
          merge_reason: mergeReason,
          merged_data: mergeData,
        })

        // Delete source client
        await supabase.from('clients').delete().eq('id', sourceId)
      }

      // Reset form
      setSelectedClients([])
      setTargetClient(null)
      setSearchTerm('')
      setSearchResults([])
      setMergeReason('')

      if (onMergeComplete) {
        onMergeComplete()
      }

      success('Selected clients have been merged into the target client.', {
        title: 'Clients Merged',
      })
    } catch (error) {
      console.error('Error merging clients:', error)
      showError(error.message || 'Failed to merge clients', {
        title: 'Merge Failed',
      })
    } finally {
      setMerging(false)
    }
  }

  if (!isAdmin) {
    return (
      <Card variant="soft" className="p-6 text-center">
        <AlertTriangle size={32} className="mx-auto mb-2 text-yellow-500" />
        <p className="text-sm text-gray-600">Only administrators can merge clients</p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Users size={20} />
        <h3 className="text-lg font-semibold text-gray-900">Merge/Deduplicate Clients</h3>
      </div>

      <Card variant="soft" className="p-4">
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search clients by name, email, or phone..."
              className="pl-10"
            />
          </div>

          {/* Search Results */}
          <AnimatePresence>
            {searchResults.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2 max-h-60 overflow-y-auto"
              >
                {searchResults.map((client) => {
                  const isSelected = selectedClients.find((c) => c.id === client.id)
                  const isTarget = targetClient?.id === client.id
                  return (
                    <div
                      key={client.id}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isTarget
                          ? 'border-primary-500 bg-primary-50'
                          : isSelected
                          ? 'border-primary-300 bg-primary-50/50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleClient(client)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-400 to-secondary-400 flex items-center justify-center text-white text-sm font-bold">
                            {getInitials(`${client.first_name} ${client.last_name}`)}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {client.first_name} {client.last_name}
                            </p>
                            <p className="text-xs text-gray-500">{client.email || client.phone || 'No contact'}</p>
                          </div>
                        </div>
                        {isTarget && (
                          <div className="flex items-center gap-1 text-primary-600 text-xs font-semibold">
                            <CheckCircle2 size={14} />
                            Target
                          </div>
                        )}
                      </div>
                      {isSelected && !isTarget && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={(e) => {
                            e.stopPropagation()
                            setAsTarget(client)
                          }}
                          className="mt-2 w-full"
                        >
                          Set as Target
                        </Button>
                      )}
                    </div>
                  )
                })}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Selected Clients Summary */}
          {selectedClients.length > 0 && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-semibold text-gray-700 mb-2">
                Selected: {selectedClients.length} client(s)
              </p>
              {targetClient && (
                <p className="text-xs text-primary-600 mb-3">
                  Target: {targetClient.first_name} {targetClient.last_name}
                </p>
              )}
              <Textarea
                label="Merge Reason (Required)"
                value={mergeReason}
                onChange={(e) => setMergeReason(e.target.value)}
                placeholder="Explain why these clients are being merged..."
                rows={2}
                required
              />
              <Button
                onClick={handleMerge}
                disabled={selectedClients.length < 2 || !targetClient || !mergeReason.trim() || merging}
                className="mt-3 w-full"
              >
                {merging ? 'Merging...' : `Merge ${selectedClients.length - 1} client(s) into target`}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}

export default ClientMerge

